/* TipTap editor whose canonical value is a ProseMirror/TipTap JSON document */
/* (RichDoc) — the storage format for content-editor sections (spec 02). */
/* The markdown-backed RichEditor stays for the legacy 'cours' chat flow. */

import type { ReactNode } from 'react';
import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useEditor, EditorContent, ReactNodeViewRenderer } from '@tiptap/react';
import { Editor, Extension, type Content } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import { TextStyle, Color, FontSize } from '@tiptap/extension-text-style';
import { TextAlign } from '@tiptap/extension-text-align';
import { Markdown } from 'tiptap-markdown';
import {
  Palette,
  TextT,
  TextAlignLeft,
  TextAlignCenter,
  TextAlignRight,
  TextAlignJustify,
} from '@phosphor-icons/react';
import { InlineMath } from './inline-math-extension';
import { BlockMath } from './block-math-extension';
import { InlineMathView, BlockMathView } from './math-node-views';
import { cn } from '@/lib/utils';
import './rich-editor.css';

const InlineMathRendered = InlineMath.extend({
  addNodeView() { return ReactNodeViewRenderer(InlineMathView as Parameters<typeof ReactNodeViewRenderer>[0]); },
});
const BlockMathRendered = BlockMath.extend({
  addNodeView() { return ReactNodeViewRenderer(BlockMathView as Parameters<typeof ReactNodeViewRenderer>[0]); },
});

const SingleLine = Extension.create({
  name: 'singleLine',
  addKeyboardShortcuts() { return { Enter: () => true }; },
});

export type RichDoc = { type: 'doc'; content?: unknown[] };

export const EMPTY_RICH_DOC: RichDoc = { type: 'doc', content: [{ type: 'paragraph' }] };

/* Shared active-editor context — lets a parent render a single mutualized toolbar */
export const ActiveEditorCtx = createContext<{
  setActive: (e: Editor | null) => void;
} | null>(null);

/* One-shot markdown → RichDoc conversion for legacy `{ markdown }` text sections */
/* (migrates them to the canonical TipTap-JSON format on first load/edit). */
export function markdownToRichDoc(markdown: string): RichDoc {
  const editor = new Editor({ extensions: [StarterKit, Markdown.configure({ html: false })], content: markdown });
  const json = editor.getJSON() as RichDoc;
  editor.destroy();
  return json;
}

interface RichDocEditorProps {
  value: RichDoc | null;
  onChange: (doc: RichDoc) => void;
  editable?: boolean;
  minHeight?: number;
  singleLine?: boolean;
}

const wrapperClass = "border-[1.5px] border-transparent rounded-sm overflow-hidden bg-transparent focus-within:border-naia-muted-2 focus-within:bg-background transition-colors duration-150";

export function RichDocEditor({ value, onChange, editable = true, minHeight = 120, singleLine = false }: RichDocEditorProps) {
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const lastEmitted = useRef<string>(JSON.stringify(value ?? EMPTY_RICH_DOC));
  const lastIncoming = useRef<string>(lastEmitted.current);
  const created = useRef(false);
  const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const ctx = useContext(ActiveEditorCtx);
  const [focused, setFocused] = useState(false);

  const editor = useEditor({
    editable,
    extensions: [
      StarterKit,
      TextStyle,
      Color,
      FontSize,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      InlineMathRendered,
      BlockMathRendered,
      ...(singleLine ? [SingleLine] : []),
    ],
    content: (value ?? EMPTY_RICH_DOC) as Content,
    onCreate({ editor: e }) {
      lastEmitted.current = JSON.stringify(e.getJSON());
      created.current = true;
    },
    onUpdate({ editor: e }) {
      const json = e.getJSON() as RichDoc;
      const serialise = JSON.stringify(json);
      /* DANGEREUX SI RETIRE — TipTap emet un update au montage, sans geste de
         l'utilisateur. Propage, il declenche l'autosave de la section et ecrit
         par-dessus un contenu que l'editeur n'a jamais su charger : une section
         `text` portant {html} etait remplacee par un paragraphe vide ~0,9 s
         apres l'ouverture de la page. Mesure : devtools/ui-audit/perte-silencieuse.mjs. */
      if (!created.current) {
        lastEmitted.current = serialise;
        return;
      }
      if (serialise === lastEmitted.current) return;
      lastEmitted.current = serialise;
      lastIncoming.current = serialise;
      onChangeRef.current(json);
    },
    onFocus({ editor: e }) {
      if (blurTimer.current) clearTimeout(blurTimer.current);
      setFocused(true);
      ctx?.setActive(e);
    },
    onBlur() {
      blurTimer.current = setTimeout(() => {
        setFocused(false);
        ctx?.setActive(null);
      }, 150);
    },
  });

  /* Sync external changes (LLM SSE / reload) without clobbering the cursor. */
  useEffect(() => {
    if (!editor) return;
    const incoming = JSON.stringify(value ?? EMPTY_RICH_DOC);
    if (incoming === lastIncoming.current) return;
    lastIncoming.current = incoming;
    if (incoming !== lastEmitted.current) {
      editor.commands.setContent((value ?? EMPTY_RICH_DOC) as Content, { emitUpdate: false });
      lastEmitted.current = JSON.stringify(editor.getJSON());
    }
  }, [editor, value]);

  useEffect(() => { editor?.setEditable(editable); }, [editor, editable]);

  useEffect(() => () => {
    if (blurTimer.current) clearTimeout(blurTimer.current);
  }, []);

  return (
    <div className={editable ? wrapperClass : "bg-transparent"}>
      <div className={cn("p-[8px_12px]", editable && "cursor-text")} style={{ minHeight: focused ? minHeight : undefined }} onClick={() => editable && editor?.commands.focus()}>
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}

/* Standalone toolbar — renders buttons for a given TipTap editor instance.    */
/* Intended to be rendered once at page level via ActiveEditorCtx.             */
const btnClass = "bg-transparent text-[var(--naia-text-secondary)] border border-transparent rounded-sm font-[inherit] font-bold text-[13px] w-8 h-8 cursor-pointer leading-none inline-flex items-center justify-center hover:text-[var(--naia-text-primary)] transition-colors";
const btnActiveClass = "bg-[var(--naia-sky)] text-naia-blue border-transparent";

const PRESET_COLORS: ReadonlyArray<{ hex: string; label: string }> = [
  { hex: '#e03e3e', label: 'Rouge — définition / terme clé' },
  { hex: '#2563eb', label: 'Bleu — exemple' },
  { hex: '#d97706', label: 'Orange — point d\'attention' },
];

const FONT_SIZES: ReadonlyArray<{ value: string; glyph: string; label: string }> = [
  { value: '0.9em', glyph: 'S', label: 'Petit' },
  { value: '', glyph: 'M', label: 'Taille normale' },
  { value: '1.25em', glyph: 'L', label: 'Grand' },
  { value: '1.5em', glyph: 'XL', label: 'Très grand' },
];

const ALIGNMENTS: ReadonlyArray<{ value: string; label: string; Icon: typeof TextAlignLeft }> = [
  { value: 'left', label: 'Aligner à gauche', Icon: TextAlignLeft },
  { value: 'center', label: 'Centrer', Icon: TextAlignCenter },
  { value: 'right', label: 'Aligner à droite', Icon: TextAlignRight },
  { value: 'justify', label: 'Justifier', Icon: TextAlignJustify },
];

const sep = <div className="w-px h-4 bg-[var(--naia-line)] mx-0.5 shrink-0" />;

export function RichToolbar({ editor }: { editor: Editor | null }) {
  if (!editor) return null;
  const btn = (active: boolean, title: string, glyph: ReactNode, onClick: () => void) => (
    <button key={title} type="button" title={title} onClick={onClick} className={cn(btnClass, active && btnActiveClass)} onMouseDown={(e) => e.preventDefault()}>
      {glyph}
    </button>
  );
  const style = editor.getAttributes('textStyle') as { color?: string; fontSize?: string };
  const applyColor = (hex: string) => editor.chain().focus().setColor(hex).run();
  const blockAlign =
    (editor.getAttributes('heading').textAlign as string | undefined) ??
    (editor.getAttributes('paragraph').textAlign as string | undefined) ??
    'left';
  return (
    <>
      {btn(editor.isActive('bold'), 'Gras', 'B', () => editor.chain().focus().toggleBold().run())}
      {btn(editor.isActive('italic'), 'Italique', 'I', () => editor.chain().focus().toggleItalic().run())}
      {sep}
      {btn(editor.isActive('heading', { level: 2 }), 'Titre 2', 'H2', () => editor.chain().focus().toggleHeading({ level: 2 }).run())}
      {btn(editor.isActive('heading', { level: 3 }), 'Titre 3', 'H3', () => editor.chain().focus().toggleHeading({ level: 3 }).run())}
      {sep}
      {btn(editor.isActive('bulletList'), 'Liste à puces', '•', () => editor.chain().focus().toggleBulletList().run())}
      {btn(editor.isActive('orderedList'), 'Liste numérotée', '1.', () => editor.chain().focus().toggleOrderedList().run())}
      {sep}
      {ALIGNMENTS.map(({ value, label, Icon }) =>
        btn(blockAlign === value, label, <Icon size={15} weight="bold" />, () =>
          editor.chain().focus().setTextAlign(value).run(),
        ),
      )}
      {sep}
      {FONT_SIZES.map((s) =>
        btn(
          s.value ? style.fontSize === s.value : !style.fontSize,
          `${s.label} (${s.glyph})`,
          <span className="text-[11px]">{s.glyph}</span>,
          () => (s.value ? editor.chain().focus().setFontSize(s.value).run() : editor.chain().focus().unsetFontSize().run()),
        ),
      )}
      {sep}
      <Palette size={15} weight="bold" className="text-[var(--naia-text-secondary)] mx-0.5 shrink-0" aria-hidden />
      {PRESET_COLORS.map((c) => (
        <button
          key={c.hex}
          type="button"
          title={c.label}
          aria-label={c.label}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => applyColor(c.hex)}
          className={cn(
            'w-5 h-5 rounded-full shrink-0 cursor-pointer transition-transform hover:scale-110',
            style.color?.toLowerCase() === c.hex ? 'ring-2 ring-[var(--naia-blue)] ring-offset-1' : 'border border-[var(--naia-line)]',
          )}
          style={{ backgroundColor: c.hex }}
        />
      ))}
      <label title="Couleur personnalisée" className="w-6 h-6 shrink-0 inline-flex items-center justify-center cursor-pointer">
        <input
          type="color"
          value={style.color ?? '#000000'}
          onChange={(e) => applyColor(e.target.value)}
          className="w-4 h-4 p-0 border-0 bg-transparent cursor-pointer"
          aria-label="Couleur personnalisée"
        />
      </label>
      {btn(false, 'Enlever la couleur', <TextT size={15} weight="bold" />, () => editor.chain().focus().unsetColor().run())}
    </>
  );
}
